/**
 * @fileoverview Badge estandarizado para indicar el estado de features según el plan
 * @module components/subscription/FeatureBadge
 * 
 * Badge único y consistente para toda la aplicación.
 * Muestra el estado de una feature: Bloqueada, Limitada o Premium.
 */

import React from 'react';

export type BadgeVariant = 'blocked' | 'limited' | 'premium';
export type BadgeSize = 'sm' | 'md' | 'lg';

interface FeatureBadgeProps {
  /**
   * Variante del badge:
   * - 'blocked': Feature completamente bloqueada (límite = -1)
   * - 'limited': Feature con límite específico (límite > 0)
   * - 'premium': Feature disponible en plan superior (general)
   */
  variant: BadgeVariant;
  
  /**
   * Tamaño del badge
   */
  size?: BadgeSize;
  
  /**
   * Clase CSS adicional
   */
  className?: string;
  
  /**
   * Texto personalizado (opcional, usa default según variant)
   */
  text?: string;
}

const VARIANT_CONFIG = {
  blocked: {
    icon: '🔒',
    text: '', // Sin texto, solo candado
    className: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  },
  limited: {
    icon: '⚡',
    text: 'Limitado',
    className: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  },
  premium: {
    icon: '👑',
    text: 'PRO',
    className: 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  },
};

const SIZE_CONFIG = {
  sm: 'px-1.5 py-0.5 text-[10px] gap-1',
  md: 'px-2 py-1 text-xs gap-1',
  lg: 'px-3 py-1.5 text-sm gap-1.5',
};

/**
 * Badge estandarizado para features del plan
 * 
 * @example
 * // Feature bloqueada
 * <FeatureBadge variant="blocked" />
 * 
 * @example
 * // Feature limitada
 * <FeatureBadge variant="limited" text="3/3" size="md" />
 * 
 * @example
 * // Feature premium
 * <FeatureBadge variant="premium" size="lg" />
 */
export function FeatureBadge({ 
  variant, 
  size = 'md', 
  className = '',
  text,
}: FeatureBadgeProps) {
  const config = VARIANT_CONFIG[variant];
  const sizeClass = SIZE_CONFIG[size];
  const displayText = text || config.text;

  return (
    <span 
      className={`
        inline-flex items-center justify-center
        font-semibold rounded-full border
        ${config.className}
        ${sizeClass}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      title={`Esta función está ${variant === 'blocked' ? 'bloqueada' : config.text.toLowerCase()}`}
    >
      <span className="leading-none">{config.icon}</span>
      {displayText && <span className="leading-none">{displayText}</span>}
    </span>
  );
}

/**
 * Helper: Determina qué badge mostrar según el límite
 * 
 * @param limit - Límite de la feature (-1, 0, o N)
 * @param currentCount - Uso actual (opcional)
 * @returns Variante del badge a mostrar o null si no necesita badge
 * 
 * @example
 * const badgeVariant = getBadgeVariantFromLimit(-1); // 'blocked'
 * const badgeVariant = getBadgeVariantFromLimit(3, 2); // 'limited'
 * const badgeVariant = getBadgeVariantFromLimit(0); // null (ilimitado)
 */
export function getBadgeVariantFromLimit(
  limit: number | undefined,
  currentCount?: number
): BadgeVariant | null {
  if (limit === undefined || limit === -1) {
    return 'blocked';
  }
  
  if (limit === 0) {
    return null; // Ilimitado, no necesita badge
  }
  
  // Limitado: solo mostrar badge si está cerca del límite (80% o más)
  if (currentCount !== undefined) {
    const usage = currentCount / limit;
    if (usage >= 0.8) return 'limited'; // Cerca del límite
  }
  
  // Si tiene límite pero no está cerca, NO mostrar badge
  return null;
}

/**
 * Helper: Texto descriptivo del límite
 */
export function getLimitText(limit: number | undefined, currentCount?: number): string {
  if (limit === undefined || limit === -1) {
    return 'Bloqueado';
  }
  
  if (limit === 0) {
    return 'Ilimitado';
  }
  
  if (currentCount !== undefined) {
    return `${currentCount}/${limit}`;
  }
  
  return `Hasta ${limit}`;
}

export default FeatureBadge;
